/**
 * RICH TEXT FIELD — campo HTML riutilizzabile con display sanificato + edit
 * inline (doppio-click) via RichTextEditor.
 *
 * Generalizza il pattern del campo `message` di LinkedinPostEditFormBody per
 * riuso sui campi testuali delle attività (note/email/chiamata/riunione: campo
 * `description`; task: campo `notes`), che passano da textarea a WYSIWYG.
 *
 *  - Display: HTML sanificato con DOMPurify (anti stored-XSS), come i commenti
 *    e i post LinkedIn.
 *  - Edit: doppio-click (o Enter/Space) -> RichTextEditor con Salva/Annulla.
 *  - `onCommit` riceve l'HTML e DEVE rigettare (throw) su errore per restare in
 *    edit mode; il chiamante mostra il toast (vedi `commitField` dei
 *    *EditFormBody).
 *  - `readOnly`: solo il display, senza doppio-click (chi non può modificare).
 */

import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { htmlHasText } from '@crm/shared';
import Button from '@mui/material/Button';
import { RichTextEditor } from './RichTextEditor';

interface RichTextFieldProps {
  /** HTML corrente (o testo semplice legacy). null/'' = vuoto. */
  value: string | null;
  /** Salva il nuovo HTML. Deve rigettare su errore (il campo resta in edit). */
  onCommit: (html: string) => Promise<void>;
  placeholder?: string;
  /** Testo mostrato quando il valore è vuoto (default: il placeholder). */
  emptyLabel?: string;
  /** Se true, il salvataggio è bloccato quando il contenuto è vuoto (senza
   *  testo reale). Preserva il vincolo `required` dei vecchi EditableField. */
  required?: boolean;
  /** Solo lettura: niente doppio-click, niente editor. */
  readOnly?: boolean;
}

export function RichTextField({ value, onCommit, placeholder, emptyLabel, required, readOnly }: RichTextFieldProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);

  function startEdit() {
    setDraft(value ?? '');
    setEditing(true);
  }

  function cancel() {
    setDraft(value ?? '');
    setEditing(false);
  }

  async function save() {
    setSaving(true);
    try {
      await onCommit(draft);
      setEditing(false);
    } catch {
      // onCommit ha già mostrato il toast: restiamo in edit per non perdere il draft.
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div className="rich-text-field rich-text-field--editing">
        <RichTextEditor
          initialHtml={value ?? ''}
          onChange={setDraft}
          placeholder={placeholder}
          autoFocus
        />
        <div className="flex gap-sm mt-sm">
          <Button
            size="small"
            variant="contained"
            onClick={save}
            disabled={saving || (required && !htmlHasText(draft))}
          >
            {t('common.save')}
          </Button>
          <Button size="small" variant="outlined" onClick={cancel} disabled={saving}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    );
  }

  const hasValue = !!value && value.trim().length > 0;
  // Props comuni alle due varianti (con/senza valore): un div "message-body"
  // cliccabile (doppio-click o Enter/Space) per entrare in edit.
  const className = `message-body rich-text-field${hasValue ? '' : ' rich-text-field--empty'}`;
  const viewProps = readOnly ? { className } : {
    className,
    role: 'button' as const,
    tabIndex: 0,
    title: t('editableField.doubleClickToEdit'),
    onDoubleClick: startEdit,
    onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        startEdit();
      }
    },
  };

  // Un nodo con dangerouslySetInnerHTML non può avere children: due varianti.
  return hasValue ? (
    <div {...viewProps} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }} />
  ) : (
    <div {...viewProps}>
      <span className="text-muted">{emptyLabel ?? placeholder ?? ''}</span>
    </div>
  );
}
